/** @format */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	Inject,
	Input,
	input,
	OnDestroy,
	OnInit,
	Signal
} from '@angular/core';
import {
	refreshProductList,
	selectIsInitialPageLoad,
	selectSelectedFilters,
	setFiltering,
	setIsInitialPageLoad,
	setRefinementUpdateObj,
	selectFulfillmentFacetCounts
} from '@plp/data-access/state';
import { shippingOptions } from '@plp/data-access/constants';
import { Store } from '@ngrx/store';
import { UtilityService, WINDOW } from '@shared/data-access/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HeaderStoreSelection } from '@shared/data-access/interfaces';
import {
	homefieldIconBuildingStore,
	HomefieldIconLibraryModule,
	homefieldIconOrderCartStandard,
	HomefieldIconsLibraryService,
	homefieldIconVehicleShipping
} from '@dsg-tech/homefield-angular-icons';
import { HomefieldTextLinkModule } from '@homefield';
import { MediaModule } from '@shared/media/components';
import { ShippingOptionButtonComponent } from '../shipping-option-button/shipping-option-button.component';
import { NgClass, TitleCasePipe } from '@angular/common';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { selectUserStoreInfo, selectUserZipCode } from '@state/data-access';

@Component({
	selector: 'plp-components-shipping-filter',
	imports: [
		NgClass,
		TitleCasePipe,
		HomefieldIconLibraryModule,
		HomefieldTextLinkModule,
		MediaModule,
		ShippingOptionButtonComponent,
		HmfActionTextComponent
	],
	templateUrl: './shipping-options.component.html',
	styleUrl: './shipping-options.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShippingOptionsComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() disabled = false;
	@Input() filtersOpen = false;
	@Input() isMobile = false;
	@Input() isInitialPageLoad = true;

	readonly sameDayDeliveryEnabled = input(false);
	readonly fulfillmentFacetsEnabled = input(false);
	readonly exposedFiltersEnabled = input(false);

	private readonly UNSUBSCRIBE$ = new Subject<void>();

	readonly selectedFilters$ = this.store
		.select(selectSelectedFilters)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly userZipCode: Signal<string> = this.store.selectSignal(selectUserZipCode);
	readonly userStoreInfo: Signal<HeaderStoreSelection | null> =
		this.store.selectSignal(selectUserStoreInfo);
	readonly isInitialPageLoad$ = this.store
		.select(selectIsInitialPageLoad)
		.pipe(takeUntil(this.UNSUBSCRIBE$));

	readonly fulfillmentFacets = this.store.selectSignal(selectFulfillmentFacetCounts);

	readonly showFulfillmentCount = computed(
		() => this.fulfillmentFacetsEnabled() && Object.keys(this.fulfillmentFacets()).length > 0
	);

	selectedOptions = [];
	FILTER_NAME = 'facetStore';
	shippingOptions = shippingOptions;
	shippingOptionsKeys: string[] = Object.keys(shippingOptions);
	mobileStoreName = '';
	locationLabel = '';

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly store: Store,
		@Inject(WINDOW) protected window: Window,
		private readonly utilityService: UtilityService,
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService
	) {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconVehicleShipping,
			homefieldIconBuildingStore,
			homefieldIconOrderCartStandard
		]);

		effect(() => {
			const zip = this.userZipCode();
			const store = this.userStoreInfo();

			if (zip || store) {
				this.setSelectedAvailabilityFilter();
				this.setMobileStoreNameAndLabel();
			}
		});
	}

	ngOnInit(): void {
		//Filter keys: if SDD enabled, remove 'Shopping In-Store' else remove 'Same Day Delivery'
		//Ship is in it's own container if SDD flag is off.
		this.shippingOptionsKeys = this.shippingOptionsKeys.filter((key) =>
			this.sameDayDeliveryEnabled() ? key !== 'ISA' : key !== 'SDD'
		);

		this.isInitialPageLoad$.subscribe((isInitialPageLoad) => {
			this.isInitialPageLoad = isInitialPageLoad;
		});

		this.selectedFilters$.subscribe((options: Record<string, string[]>) => {
			if (options[this.FILTER_NAME]) {
				this.selectedOptions = Object.assign([], options[this.FILTER_NAME]);
				this.setMobileStoreNameAndLabel();
			} else {
				this.selectedOptions = [];
			}
		});

		this.setMobileStoreNameAndLabel();
		this.cdr.detectChanges();
	}

	ngAfterViewInit(): void {
		if (this.isInitialPageLoad) {
			this.store.dispatch(setIsInitialPageLoad({ isInitialPageLoad: false }));
		}
	}

	setSelectedFilter(filterName: string): void {
		if (this.disabled) {
			return;
		}

		if (this.selectedOptions.includes(filterName)) {
			this.selectedOptions = this.selectedOptions.filter((e) => e !== filterName);
		} else {
			this.selectedOptions = [filterName];

			if (filterName === 'BOPIS' && !this.userStoreInfo()) {
				this.openStorePanel();
			} else if (filterName !== 'BOPIS' && !this.userZipCode()) {
				this.openZipCodePanel();
			}
		}
		//Check selectUserZipCode for Ship and SDD or selectedStoreInfo for PickUp exists before filetering
		if (
			(this.userZipCode() && filterName !== 'BOPIS') ||
			(this.userStoreInfo() && filterName === 'BOPIS') ||
			!this.sameDayDeliveryEnabled()
		) {
			this.setSelectedAvailabilityFilter();

			//Trigger analytics event only if filter is selected
			if (this.selectedOptions.includes(filterName)) {
				this.setAnalyticsInfo(filterName);
			}
			this.store.dispatch(refreshProductList());
		}
		this.setMobileStoreNameAndLabel();

		const plpContainerElem = document.getElementById('plp-list-container');
		this.utilityService.scrollToElement(plpContainerElem, { behavior: 'instant' });
	}

	private setSelectedAvailabilityFilter(): void {
		if (this.isInitialPageLoad) {
			return;
		}
		this.store.dispatch(
			setFiltering({
				selectedFilters: {
					[this.FILTER_NAME]: this.selectedOptions
				}
			})
		);
	}

	openStorePanel(): void {
		if (this.window?.headerFunctions?.storeSelection) {
			this.window.headerFunctions.storeSelection();
		}
	}

	openZipCodePanel(): void {
		if (this.window?.headerFunctions?.sameDayDelivery) {
			this.window.headerFunctions.sameDayDelivery();
		}
	}

	setAnalyticsInfo(filterName: string): void {
		const refinementUpdateObj = {
			UpdateType: 'Refinement',
			UpdateValue: {
				RefinementValue: filterName,
				RefinementAttribute: 'facetStore',
				ProductCountTotal: undefined
			}
		};
		this.store.dispatch(setRefinementUpdateObj({ refinementUpdateObj }));
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	keepOriginalOrder = (a, b) => a.key;

	/**
	 * @description
	 * Gets called on select store or delivery location link clicked on mobile
	 */
	selectUserZipCodeAndStoreInfo(): void {
		if (this.selectedOptions.includes('SHIP') || this.selectedOptions.includes('SDD')) {
			this.openZipCodePanel();
		} else {
			this.openStorePanel();
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	trackByFn(index: number, item: string): string {
		return index.toString() + item;
	}

	private setMobileStoreNameAndLabel(): void {
		if (this.selectedOptions.includes('SHIP')) {
			this.locationLabel = 'Ship To';
			this.mobileStoreName = this.userZipCode() || 'Update Delivery Location';
		} else if (this.selectedOptions.includes('BOPIS')) {
			this.locationLabel = 'Pickup at';
			this.mobileStoreName = this.userStoreInfo()?.name || 'Select Store';
		} else if (!this.sameDayDeliveryEnabled()) {
			this.locationLabel = 'My Store';
			this.mobileStoreName = this.userStoreInfo()?.name || 'Select Store';
		} else {
			if (this.selectedOptions.includes('BOPIS')) {
				this.locationLabel = 'Pickup at';
			} else {
				this.locationLabel = this.selectedOptions.includes('SDD')
					? 'Deliver To'
					: 'Ship To';
			}
			this.mobileStoreName = this.setDeliveryLocationAndStoreInfo();
		}
	}

	private setDeliveryLocationAndStoreInfo(): string {
		if (this.selectedOptions.includes('BOPIS')) {
			return this.userStoreInfo()?.name || (this.sameDayDeliveryEnabled() && 'Select Store');
		} else {
			return (
				this.userZipCode() || (this.sameDayDeliveryEnabled() && 'Update Delivery Location')
			);
		}
	}
}
